import { create, all, type MathNode } from 'mathjs';

/**
 * Formula engine shared by every InactiveCalc evaluation (live form, submit, seed).
 *
 * Formulas are mathjs expressions. Parameters can be referenced in two ways:
 *  - directly by name: `length * width` (root parameter) or `zone1.length` (element-scoped parameter);
 *  - through an alias declared in `calculation.variables`: `{ a: { name: 'refuelingProfile:table' } }`.
 * Aliases are required for parameter names that are not valid mathjs identifiers
 * (names containing `:`, `.`, `-`, spaces, ...) and for names mathjs already
 * defines (`pi`, `e`, `phi`, `max`, ...): a bare built-in name always keeps its
 * mathjs meaning, so existing formulas never change value when a parameter with
 * that name is added. An alias always wins over a parameter of the same name.
 *
 * Table-shaped values (`number[][]`) enter the scope untouched, so the mathjs
 * matrix functions apply: `max(column(t, 1))`, `sum(column(t, 2))`, `size(t)`.
 * Formulas may also return a table when the InactiveCalc declares `arrays`
 * (e.g. `dotMultiply(t, k)` or `scaleColumn(t, 2, k)`). `interpolate` and
 * `resample` look values up in a table and keep the shape of their argument,
 * so profiles can be rebuilt in one expression. Column indices in formulas
 * are 1-based (mathjs convention).
 */

type Row = ReadonlyArray<number>;
type TableLike = ReadonlyArray<Row> | { toArray: () => Array<Array<number>> };

const toRows = (table: TableLike): ReadonlyArray<Row> =>
  Array.isArray(table) ? table : (table as { toArray: () => Array<Array<number>> }).toArray();

/**
 * Sums a table's values over time: each value holds from its row's time until
 * the next row's time (or `stopTime` for the last row), and the result is the
 * sum of value × duration up to `stopTime`. Rows at or after `stopTime` are
 * ignored. Rows must be sorted by time. Columns are 1-based.
 *
 * Example, stop time 24: [[0, 2], [1, 3], [3, 1]] → 1·2 + 2·3 + 21·1 = 29.
 */
export const sumOverTime = (
  table: TableLike,
  stopTime: number,
  timeColumn: number = 1,
  valueColumn: number = 2,
): number => {
  const rows = toRows(table).filter((row) => row[timeColumn - 1] < stopTime);
  return rows.reduce((total, row, index) => {
    const start = row[timeColumn - 1];
    const end = index + 1 < rows.length ? Math.min(rows[index + 1][timeColumn - 1], stopTime) : stopTime;
    return total + row[valueColumn - 1] * (end - start);
  }, 0);
};

/**
 * Multiplies one column of a table by a scalar; other columns are unchanged.
 * Column index is 1-based. For every cell, use mathjs `dotMultiply(t, k)`.
 */
export const scaleColumn = (table: TableLike, column: number, factor: number): number[][] =>
  toRows(table).map((row) => row.map((cell, index) => (index === column - 1 ? cell * factor : cell)));

// A number, a vector or a one-column table (plain or mathjs Matrix).
type Lookup = number | ReadonlyArray<number> | ReadonlyArray<Row> | { toArray: () => unknown };

// Applies `fn` per element and keeps the input's shape, so lookups compose with concat and arithmetic.
// A table argument must have exactly one column; anything wider is a configuration error.
const mapLookup = (input: Lookup, fn: (x: number) => number): number | number[] | number[][] => {
  const value = (
    typeof input === 'object' && !Array.isArray(input) ? (input as { toArray: () => unknown }).toArray() : input
  ) as number | ReadonlyArray<number | Row>;
  if (typeof value === 'number') return fn(value);
  return value.map((entry) => {
    if (typeof entry === 'number') return fn(entry);
    if (entry.length !== 1) throw new Error('Lookup argument must be a number, a vector or a one-column table');
    return entry.map(fn);
  }) as number[] | number[][];
};

/**
 * Piecewise-linear lookup of `x` in a table sorted by `xColumn`. Outside the
 * table's range the first or last row's value is held, so a lookup one step
 * past the end never silently becomes 0. An empty table gives 0. `x` may be a
 * number, a vector or a one-column table and the result keeps that shape.
 * Columns are 1-based.
 */
export const interpolate = (table: TableLike, x: Lookup, xColumn: number = 1, yColumn: number = 2) => {
  const rows = toRows(table);
  // TODO: linear scan per lookup; switch to binary search if tables grow past a few hundred rows.
  const at = (value: number): number => {
    if (rows.length === 0) return 0;
    if (value <= rows[0][xColumn - 1]) return rows[0][yColumn - 1];
    const next = rows.findIndex((row) => row[xColumn - 1] >= value);
    if (next < 0) return rows[rows.length - 1][yColumn - 1];
    if (rows[next][xColumn - 1] === value) return rows[next][yColumn - 1];
    const [x0, y0] = [rows[next - 1][xColumn - 1], rows[next - 1][yColumn - 1]];
    const [x1, y1] = [rows[next][xColumn - 1], rows[next][yColumn - 1]];
    return y0 + ((value - x0) / (x1 - x0)) * (y1 - y0);
  };
  return mapLookup(x, at);
};

/**
 * Step lookup: the value of the last row at or before each time, 0 before the
 * first row. Rows must be sorted by `timeColumn`. `times` may be a number, a
 * vector or a one-column table and the result keeps that shape.
 */
export const resample = (table: TableLike, times: Lookup, timeColumn: number = 1, valueColumn: number = 2) => {
  const rows = toRows(table);
  const at = (time: number): number => {
    let value = 0;
    for (const row of rows) {
      if (row[timeColumn - 1] > time) break;
      value = row[valueColumn - 1];
    }
    return value;
  };
  return mapLookup(times, at);
};

/** Functions available in formulas on top of the mathjs standard library. */
export const customFormulaFunctions = { sumOverTime, scaleColumn, interpolate, resample };

// Own instance, so the custom functions do not leak into other mathjs users.
export const formulaMath = create(all);
formulaMath.import(customFormulaFunctions);

/**
 * True for names mathjs defines (constants, functions, custom functions).
 * A parameter with such a name must be referenced through an alias.
 * Instance properties like `config` or `import` count as reserved too; mathjs
 * would resolve them from its namespace as well.
 */
export const isReservedFormulaName = (name: string): boolean => Object.prototype.hasOwnProperty.call(formulaMath, name);

// Parsing is the slow part of mathjs, so compile each formula once.
const compiledFormulas = new Map<string, { evaluate: (scope: Record<string, unknown>) => unknown }>();
export const compileFormula = (formula: string) => {
  let compiled = compiledFormulas.get(formula);
  if (!compiled) {
    compiled = formulaMath.compile(formula);
    compiledFormulas.set(formula, compiled);
  }
  return compiled;
};

export type FormulaReference =
  // `name`
  | { kind: 'symbol'; symbol: string }
  // `element.name`
  | { kind: 'member'; symbol: string; property: string };

const referenceKey = (reference: FormulaReference) =>
  reference.kind === 'symbol' ? reference.symbol : `${reference.symbol}.${reference.property}`;

const referencesCache = new Map<string, ReadonlyArray<FormulaReference>>();

/**
 * Identifiers a formula reads, taken from its AST. Function names are not
 * references. An unparsable formula has no references; evaluation reports the error.
 */
export const getFormulaReferences = (formula: string): ReadonlyArray<FormulaReference> => {
  const cached = referencesCache.get(formula);
  if (cached) return cached;

  const references = new Map<string, FormulaReference>();
  const add = (reference: FormulaReference) => references.set(referenceKey(reference), reference);

  let root: MathNode | undefined;
  try {
    root = formulaMath.parse(formula);
  } catch {
    root = undefined;
  }

  // `any`: the mathjs node typings do not expose `index.dotNotation` or `object`.
  root?.traverse((node: any, path: string | null, parent: any) => {
    if (node.type === 'SymbolNode') {
      const isFunctionName = parent?.type === 'FunctionNode' && path === 'fn';
      // `zone1.length` is a member reference; `table[1, 2]` still reads the symbol `table`.
      const isMemberObject = parent?.type === 'AccessorNode' && path === 'object' && parent.index?.dotNotation;
      if (!isFunctionName && !isMemberObject) add({ kind: 'symbol', symbol: node.name });
      return;
    }
    if (node.type === 'AccessorNode' && node.object?.type === 'SymbolNode' && node.index?.dotNotation) {
      const property = node.index.dimensions?.[0];
      if (property?.type === 'ConstantNode' && typeof property.value === 'string') {
        add({ kind: 'member', symbol: node.object.name, property: property.value });
      }
    }
  });

  const result = Array.from(references.values());
  referencesCache.set(formula, result);
  return result;
};
